import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setAuthToken } from '../api';
import { Lock, User, Shield, ArrowRight, Eye, EyeOff } from 'lucide-react';

export default function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);

            const response = await api.post('/token', formData, {
                // Don't let the 401 interceptor redirect during login attempt
                validateStatus: (status) => status < 500,
            });

            if (response.status === 200) {
                // Store token as Bearer fallback (in case HttpOnly cookie doesn't work
                // due to self-signed SSL, Cloudflare proxy, or SameSite restrictions)
                const token = response.data?.access_token;
                if (token) {
                    setAuthToken(token);
                }
                // Verify auth works by testing /users/me
                try {
                    await api.get('/users/me');
                } catch (verifyErr) {
                    console.warn('Auth verification after login:', verifyErr);
                }
                navigate('/');
            } else if (response.status === 401) {
                setError(response.data?.detail || 'Invalid username or password');
            } else if (response.status === 429) {
                setError('Too many login attempts. Please wait a minute.');
            } else {
                setError(`Login failed (${response.status})`);
            }
        } catch (err) {
            console.error('Login error:', err);
            if (err.code === 'ERR_NETWORK') {
                setError('Network error — cannot reach server. Check your connection.');
            } else if (err.response?.status === 429) {
                setError('Too many login attempts. Please wait a minute.');
            } else if (err.response?.status === 401) {
                setError(err.response?.data?.detail || 'Invalid username or password');
            } else if (err.response?.status >= 500) {
                setError(`Server error (${err.response.status}) — try again in a moment`);
            } else {
                setError(err.response?.data?.detail || `Login failed (${err.message})`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
             style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 30%, #0f172a 50%, #164e63 80%, #0f172a 100%)' }}>
            
            {/* Animated background orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-[0.03]" 
                     style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
            </div>

            <div className="relative z-10 w-full max-w-md animate-slide-up">
                {/* Logo / Brand */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25 mb-4">
                        <Shield className="text-white" size={28} />
                    </div>
                    <h1 className="text-3xl font-black text-white mb-1 tracking-tight">Welcome Back</h1>
                    <p className="text-slate-400 text-sm">Sign in to the admin panel</p>
                </div>

                {/* Login Card */}
                <div className="glass rounded-3xl p-8 glow-blue">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm text-center animate-scale-in flex items-center justify-center gap-2">
                            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="space-y-5">
                        <div>
                            <label className={`block text-xs font-semibold mb-2 transition-colors duration-200 uppercase tracking-wider ${focusedField === 'username' ? 'text-blue-400' : 'text-slate-500'}`}>
                                Username
                            </label>
                            <div className="relative group">
                                <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-200 ${focusedField === 'username' ? 'text-blue-400' : 'text-slate-500'}`}>
                                    <User size={18} />
                                </div>
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    autoComplete="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    onFocus={() => setFocusedField('username')}
                                    onBlur={() => setFocusedField(null)}
                                    className="input-field pl-12 py-3.5 rounded-xl text-sm"
                                    placeholder="admin"
                                    required
                                />
                                <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 pointer-events-none ${focusedField === 'username' ? 'opacity-100' : 'opacity-0'}`}
                                     style={{ boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.08)' }} />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-xs font-semibold mb-2 transition-colors duration-200 uppercase tracking-wider ${focusedField === 'password' ? 'text-blue-400' : 'text-slate-500'}`}>
                                Password
                            </label>
                            <div className="relative group">
                                <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-200 ${focusedField === 'password' ? 'text-blue-400' : 'text-slate-500'}`}>
                                    <Lock size={18} />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onFocus={() => setFocusedField('password')}
                                    onBlur={() => setFocusedField(null)}
                                    className="input-field pl-12 pr-12 py-3.5 rounded-xl text-sm"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                                <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 pointer-events-none ${focusedField === 'password' ? 'opacity-100' : 'opacity-0'}`}
                                     style={{ boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.08)' }} />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-primary py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm group mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Signing In...</span>
                                </>
                            ) : (
                                <>
                                    <span>Sign In</span>
                                    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <p className="text-center text-slate-600 text-xs mt-6">
                    VPN Admin Panel · Secured Connection
                </p>
            </div>
        </div>
    );
}
